import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "../Reveal";

const EASE = [0.16, 1, 0.3, 1];

const ARTISTS = [
  {
    id: "andres",
    name: "Andres",
    role: "Resident",
    specialty: "Micro Realism · Fine Line · Black & Grey",
    bio: "ANDRES — tattooing is where craftsmanship meets storytelling. Originally from Colombia and now based in Sydney, he specialises in fine line and microrealism, drawing inspiration from classical sculpture, wildlife, and the quiet beauty of the natural world. His work is defined by clean composition, delicate detail, and a timeless aesthetic, with every tattoo thoughtfully designed to move with the body and age gracefully. Each piece is approached as a collaboration, creating artwork that is both deeply personal and made to last.",
    image: "/artist-andres.jpg",
    objectPosition: "center 30%",
    instagram: "davila_tattoos",
  },
  {
    id: "doy",
    name: "Doy",
    role: "Resident",
    specialty: "Fine Line · Micro Realism · Ornamental",
    bio: "Doy is a fine-line illustrative artist with an ornamental approach, weaving together Baroque and Art Nouveau influences. Her work blends flowing ornamental patterns with classical motifs, creating pieces that move naturally with the body and feel both intricate and timeless. Inspired by vintage and classical illustrations, she brings a distinctive aesthetic to every design.",
    image: "/artist-doy.jpg",
    objectPosition: "center",
    instagram: "doy.ttt",
  },
  {
    id: "junwon",
    name: "Junwon",
    role: "Resident",
    specialty: "Fine Line · Micro-realism · Black & Grey",
    bio: "Junwon is known for his distinctive approach to fine line and micro-realism tattooing. Blending classical influences, architectural forms and delicate geometric compositions, he creates custom black & grey tattoos that feel refined, balanced and built to age gracefully. Every piece is thoughtfully designed to complement the body's natural flow while reflecting each client's personal story.",
    image: "/artist-junwon.jpg",
    objectPosition: "center 35%",
    instagram: "j.wontattoo",
  },
  {
    id: "derrick",
    name: "Derrick",
    role: "Resident",
    specialty: "Fine Line · Micro Realism · Black & Grey",
    bio: "Derrick specialises in a style that combines fine line tattooing with elements of realism. He creates meaningful, story-driven tattoos by seamlessly connecting multiple elements into a single cohesive composition. Each piece is thoughtfully designed to reflect the client's personal story, resulting in timeless artwork with depth, symbolism, and lasting meaning.",
    image: "/artist-derrick.jpg",
    objectPosition: "60% center",
    instagram: "deos__tt",
  },
  {
    id: "hando",
    name: "Hando",
    role: "Resident",
    specialty: "Large Scale Realism · Black & Grey",
    bio: "HANDO — Korean tattoo artist who began my journey in 2020. I've always been drawn to stories. Mythology, religious traditions, and history have always been a major source of inspiration for me. Renaissance art, biblical narratives, Eastern and Western mythology, as well as classical sculpture and architecture, continue to influence the way I design and create my work. I specialize in black and grey realism, with a strong focus on fine detail and craftsmanship. Rather than simply creating an image, I aim to create tattoos with meaning, atmosphere, and timeless quality — pieces that people will be proud to wear for years to come. Now based in Australia, I'm excited to take on new challenges, work on larger and more meaningful projects, and continue growing as an artist. My goal is to keep refining my craft while creating tattoos that leave a lasting impression.",
    image: "/artist-hando.jpg",
    objectPosition: "center 30%",
    instagram: "hando_tattoo",
  },
  {
    id: "hong",
    name: "Hong",
    role: "Resident",
    specialty: "Black & Grey · Micro Realism · Fine Line",
    bio: "Hi, I'm Hong, a tattoo artist from Seoul, South Korea. I focus on Black & Grey and Fine Line tattoos, creating clean, timeless pieces with attention to detail.",
    image: "/artist-hong.jpg",
    objectPosition: "center 35%",
    instagram: "hwahongtattoo",
  },
  {
    id: "tae",
    name: "Tae",
    role: "Resident",
    specialty: "Fine Line · Micro Realism · Illustrative",
    bio: "TAE sits between illustration and traditional tattoo foundations. He hand-draws, illustration-driven elements with timeless tattoo principles — structure, flow, and longevity always come first. Each piece created is carefully composed. The approach is expressive, but controlled — refined rather than loud. Not interested in chasing trends or fast aesthetics. Tae focuses on clarity, subtle impact, and work that feels intentional instead of excessive. Tattoos that reveal their details over time, rather than demand attention instantly.",
    image: "/artist-tae.jpg",
    objectPosition: "center 30%",
    instagram: "woonamink",
  },
  {
    id: "jinnie",
    name: "Jinnie",
    role: "Resident",
    specialty: "Colour Realism · Nature",
    bio: "Jinnie is a Korean tattoo artist based in Sydney, Australia, specialising in colour realism. Her work is known for its delicate details, soft colour palettes, and nature-inspired designs, creating unique tattoos that are both elegant and timeless.",
    image: "/artist-jinnie.jpg",
    objectPosition: "center 30%",
    instagram: "tattoobyjinnie",
  },
  {
    id: "reagan",
    name: "Reagan",
    role: "Resident",
    specialty: "Fine Line · Ornamental · Script",
    bio: "I'm Reagan — a tattoo artist from Scotland. I specialise in fine line, ornamental and script tattoos. I have always been drawn to creating pieces that feel like adornments for the body, complementing its natural flow with designs that feel timeless and intentional.",
    image: "/artist-reagan.jpg",
    objectPosition: "center 30%",
  },
  {
    id: "ari",
    name: "Ari",
    role: "Apprentice",
    specialty: "Fine Line · Studies",
    bio: "In training under the JAY-INK studio standard.",
    image: "/artist-ari.jpg",
    objectPosition: "center 25%",
    instagram: "ari_ink2020",
  },
  {
    id: "miriam",
    name: "Miriam",
    role: "Management",
    specialty: "Social Manager & Bookings",
    bio: "MIRIAM — Born and raised in the big capital of Madrid, Spain. Owner of her successful business @bundeenamadrid with over 25 years experience. Her hard work, dedication to her passion and consistency to the finest detail with everything she does is of another level. She is definitely the fuel and soul of @jayink__.",
    image: "/artist-miriam.jpg",
    objectPosition: "center 30%",
  },
  {
    id: "alex",
    name: "Alex",
    role: "Management",
    specialty: "Web · Bookings · Management",
    bio: "ALEX — Behind the digital side of the studio. Designs and maintains the JAY-INK website, handles bookings coordination and supports the day-to-day operations across both studios. Quiet, precise and always a step ahead — keeping the machine running smoothly.",
    image: "/artist-alex.jpg",
    objectPosition: "center center",
  },
];

function PlaceholderAvatar() {
  return (
    <div
      className="w-full h-full bg-[#D6D6D6]"
      style={{
        backgroundImage:
          "radial-gradient(circle at 50% 38%, #0A0A0A 0 16%, transparent 17%), radial-gradient(ellipse at 50% 95%, #0A0A0A 0 28%, transparent 29%)",
      }}
      aria-label="Photo coming soon"
    />
  );
}

function ArtistCard({ artist, index }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <motion.article
      ref={ref}
      data-testid={`artist-card-${artist.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: EASE, delay: (index % 4) * 0.08 }}
      className="group cursor-default"
    >
      <div className="relative aspect-square w-full bg-[#EAEAEA] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          {artist.image ? (
            <img
              src={artist.image}
              alt={artist.name}
              loading="lazy"
              className={`w-full h-full transition-transform duration-[1500ms] ease-out group-hover:scale-105 ${artist.fit === "contain" ? "object-contain" : "object-cover"}`}
              style={{
                objectPosition: artist.objectPosition || "center",
                filter: "grayscale(100%) contrast(1.02)",
                background: artist.fit === "contain" ? "#D6D6D6" : undefined,
                transform: artist.imageScale ? `scale(${artist.imageScale})` : undefined,
                transformOrigin: "center",
              }}
            />
          ) : (
            <PlaceholderAvatar />
          )}
        </motion.div>
        <div className="absolute top-3 left-3 text-[9px] tracking-[0.3em] uppercase text-[#0A0A0A]/75 bg-white/85 backdrop-blur-sm px-2 py-1">
          {artist.role}
        </div>
        <div className="absolute inset-0 bg-[#0A0A0A]/0 group-hover:bg-[#0A0A0A]/10 transition-colors duration-700" />
      </div>

      <div className="pt-5">
        <h3
          className="text-xl md:text-2xl uppercase mb-2 text-[#0A0A0A]"
          style={{
            fontFamily: "'Jost', 'Futura', sans-serif",
            letterSpacing: "0.16em",
            fontWeight: 300,
          }}
        >
          {artist.name}
        </h3>
        <div className="overline mb-3">{artist.specialty}</div>
        <p className={`text-xs md:text-sm text-[#666] leading-relaxed font-light ${expanded ? "" : "line-clamp-3"}`}>
          {artist.bio}
        </p>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          data-testid={`artist-readmore-${artist.id}`}
          className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#999] hover:text-[#0A0A0A] transition-colors"
        >
          {expanded ? "Read Less −" : "Read More +"}
        </button>
        {artist.role !== "Management" && (
          <a
            href={`/portfolio/${artist.id}`}
            data-testid={`artist-work-${artist.id}`}
            className="mt-4 inline-block text-[10px] tracking-[0.25em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A] pb-0.5 hover:opacity-60 transition-opacity"
          >
            View Work →
          </a>
        )}
      </div>
    </motion.article>
  );
}

export default function Artists() {
  return (
    <section
      id="artists"
      data-testid="artists-section"
      className="bg-white py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="text-center mb-16 md:mb-24">
          <Reveal delay={0.1}>
            <h2
              className="font-display text-[#0A0A0A] uppercase mx-auto"
              style={{
                fontSize: "clamp(2.4rem, 8vw, 7rem)",
                letterSpacing: "0.32em",
                paddingLeft: "0.32em",
                fontWeight: 200,
                lineHeight: 1,
              }}
            >
              ARTISTS
            </h2>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-14 md:gap-x-8 md:gap-y-20">
          {ARTISTS.map((a, i) => (
            <ArtistCard key={a.id} artist={a} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
